/**
 * Reusable Filter Components for Shop Services Module
 * Provides consistent styling and behavior across Products and Sellers pages
 */

import React from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import Select from 'react-select';
import './FiltersStyles.css';

// Common styling constants
const FILTER_HEIGHT = '38px';
const FILTER_FONT_SIZE = '14px';
const BORDER_COLOR = '#e3e6f0';

// Common react-select styles
const getSelectStyles = () => ({
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any, state: any) => ({
    ...base,
    height: FILTER_HEIGHT,
    minHeight: FILTER_HEIGHT,
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: '6px',
    fontSize: FILTER_FONT_SIZE,
    boxShadow: 'none', // Remove blue focus shadow
    width: '100%',
    minWidth: '150px',
    borderColor: BORDER_COLOR, // Always use consistent border color
    '&:hover': {
      borderColor: BORDER_COLOR
    }
  }),
  valueContainer: (base: any) => ({
    ...base,
    height: '36px',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center'
  }),
  input: (base: any) => ({
    ...base,
    margin: '0',
    paddingTop: '0',
    paddingBottom: '0',
    height: 'auto'
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: '36px',
    padding: '0 8px'
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    height: '20px',
    alignSelf: 'center'
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: '0',
    svg: {
      width: '16px',
      height: '16px'
    }
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#212529',
    fontSize: FILTER_FONT_SIZE,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'calc(100% - 20px)'
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#6c757d',
    fontSize: FILTER_FONT_SIZE,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'calc(100% - 20px)'
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected 
      ? '#e9ecef'  // Light gray for selected
      : state.isFocused 
        ? '#f8f9fa'  // Lighter gray for focused
        : 'white',
    color: '#212529',
    fontSize: FILTER_FONT_SIZE,
    '&:hover': {
      backgroundColor: '#f8f9fa'
    }
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999
  })
});

// SearchBar Component
interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder = "Search by name or ID",
  width = "200px"
}) => {
  return (
    <div style={{ minWidth: width }}>
      <Form onSubmit={(e) => e.preventDefault()} style={{ position: 'relative' }}>
        <InputGroup style={{ height: FILTER_HEIGHT }}>
          <InputGroup.Text style={{ 
            height: FILTER_HEIGHT, 
            fontSize: FILTER_FONT_SIZE,
            borderColor: BORDER_COLOR,
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px'
          }}>
            <i className="ph-magnifying-glass"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ 
              paddingRight: value ? '35px' : '12px',
              height: FILTER_HEIGHT,
              fontSize: FILTER_FONT_SIZE,
              borderColor: BORDER_COLOR,
              boxShadow: 'none',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = BORDER_COLOR;
              e.target.style.boxShadow = 'none';
            }}
          />
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                color: '#6c757d',
                fontSize: '16px',
                zIndex: 2
              }}
              title="Clear search"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
        </InputGroup>
      </Form>
    </div>
  );
};

// FilterDropdown Component
interface FilterOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  value: FilterOption | null;
  onChange: (option: FilterOption | null) => void;
  options: FilterOption[];
  placeholder?: string;
  width?: string;
  isLoading?: boolean;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
  width = "150px",
  isLoading = false
}) => {
  return (
    <div style={{ 
      width,
      minWidth: width,
      flexShrink: 0
    }}>
      <Select
        classNamePrefix="react-select"
        value={value}
        onChange={onChange}
        options={options}
        isLoading={isLoading}
        placeholder={placeholder}
        menuPortalTarget={document.body}
        styles={getSelectStyles()}
      />
    </div>
  );
};

// ActionButton Component
interface ActionButtonProps {
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
  children: React.ReactNode;
  disabled?: boolean;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  variant = 'primary',
  children,
  disabled = false
}) => {
  return (
    <Button 
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      style={{ 
        height: FILTER_HEIGHT,
        fontSize: FILTER_FONT_SIZE,
        fontWeight: '500',
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '6px',
        border: variant === 'primary' ? 'none' : undefined,
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </Button>
  );
};

// FilterContainer Component
interface FilterContainerProps {
  children: React.ReactNode;
  className?: string;
}

export const FilterContainer: React.FC<FilterContainerProps> = ({
  children,
  className = ''
}) => {
  return (
    <div className={`d-flex justify-content-end align-items-end gap-3 filters-container ${className}`}>
      {children}
    </div>
  );
};